// slices/profileSlice.js
import { createSlice, createAsyncThunk, createSelector } from "@reduxjs/toolkit";
import ProfileService from "../services/ProfileService";

// ==================== HELPER: Error Handler ====================
const handleError = (error) => {
  console.log('Full error object:', JSON.stringify(error, null, 2));
  
  let errorMessage = 'An unexpected error occurred. Please try again.';
  
  if (typeof error === 'string') {
    errorMessage = error;
  } else if (error?.data?.error?.message) {
    errorMessage = error.data.error.message;
  } else if (error?.error && typeof error.error === 'string') {
    errorMessage = error.error;
  } else if (error?.message) {
    errorMessage = error.message;
  } else if (error?.response?.data?.error?.message) {
    errorMessage = error.response.data.error.message;
  } else if (error?.response?.data?.error && typeof error.response.data.error === 'string') {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.response?.data?.data?.error?.message) {
    errorMessage = error.response.data.data.error.message;
  } else if (error?.success === false && error?.error) {
    errorMessage = error.error;
  } else if (error?.data && error?.statusCode) {
    if (error.data.error?.message) {
      errorMessage = error.data.error.message;
    } else if (error.data.message) {
      errorMessage = error.data.message;
    }
  } else if (error?.response) {
    if (error.response.data?.error?.message) {
      errorMessage = error.response.data.error.message;
    } else if (error.response.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response.statusText) {
      errorMessage = error.response.statusText;
    }
  } else if (error?.error) {
    if (typeof error.error === 'string') {
      errorMessage = error.error;
    } else if (error.error.message) {
      errorMessage = error.error.message;
    }
  }
  
  console.log('Extracted error message:', errorMessage);
  return errorMessage;
};

// ==================== REQUEST DEDUPLICATION ====================
// Track pending requests to prevent duplicates
const pendingRequests = new Map();

const deduplicateRequest = (thunkName, thunkFn) => {
  return createAsyncThunk(
    thunkName,
    async (arg, thunkAPI) => {
      // Create a unique key for this request
      const requestKey = JSON.stringify({ thunkName, arg });
      
      // If there's already a pending request with this key, return its promise
      if (pendingRequests.has(requestKey)) {
        return pendingRequests.get(requestKey);
      }
      
      // Create the promise
      const promise = thunkFn(arg, thunkAPI)
        .finally(() => {
          // Clean up after the request completes
          pendingRequests.delete(requestKey);
        });
      
      // Store the promise
      pendingRequests.set(requestKey, promise);
      
      return promise;
    }
  );
};

// ==================== ASYNC THUNKS ====================

// -------- PROFILE MANAGEMENT --------

/**
 * Get user profile
 * GET /api/profile
 */
export const getProfile = createAsyncThunk(
  'profile/getProfile',
  async (_, { rejectWithValue, getState }) => {
    try {
      // Check if user is authenticated before making request
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.getProfile();
      return response;
    } catch (error) {
      // If error is 401 (unauthorized), reset the profile state
      if (error.response?.status === 401) {
        return rejectWithValue('Session expired');
      }
      return rejectWithValue(handleError(error));
    } 
  }
);

/**
 * Update profile info (name, etc.)
 * PUT /api/profile/info
 */
export const updateProfileInfo = createAsyncThunk(
  'profile/updateProfileInfo',
  async (data, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.updateProfileInfo(data);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PROFILE IMAGE --------

/**
 * Update profile image
 * POST /api/profile/image
 */
export const updateProfileImage = createAsyncThunk(
  'profile/updateProfileImage',
  async (imageFile, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.updateProfileImage(imageFile);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Remove profile image
 * DELETE /api/profile/image
 */
export const removeProfileImage = createAsyncThunk(
  'profile/removeProfileImage',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.removeProfileImage();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PUBLIC PROFILES --------

/**
 * Get public profile (for viewing by others)
 * GET /api/profile/public/:userId
 */
export const getPublicProfile = createAsyncThunk(
  'profile/getPublicProfile',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await ProfileService.getPublicProfile(userId);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Get user by email (public)
 * GET /api/profile/public/email/:email
 */
export const getPublicUserByEmail = createAsyncThunk(
  'profile/getPublicUserByEmail',
  async (email, { rejectWithValue }) => {
    try {
      const response = await ProfileService.getPublicUserByEmail(email);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- SETTINGS --------

/**
 * Get user settings
 * GET /api/profile/settings
 */
export const getUserSettings = createAsyncThunk(
  'profile/getUserSettings',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.getUserSettings();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update user settings
 * PUT /api/profile/settings
 */
export const updateSettings = createAsyncThunk(
  'profile/updateSettings',
  async (settings, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.updateSettings(settings);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update notification preferences
 * PUT /api/profile/notifications
 */
export const updateNotificationPreferences = createAsyncThunk(
  'profile/updateNotificationPreferences',
  async (preferences, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.updateNotificationPreferences(preferences);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ACTIVITY --------

/**
 * Get user activity log (last login, last active)
 * GET /api/profile/activity
 */
export const getUserActivity = createAsyncThunk(
  'profile/getUserActivity',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.getUserActivity();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Update last active timestamp
 * POST /api/profile/activity
 */
export const updateUserActivity = createAsyncThunk(
  'profile/updateUserActivity',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.updateUserActivity();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- PROFILE COMPLETION --------

/**
 * Get profile completion status
 * GET /api/profile/completion
 */
export const getProfileCompletion = createAsyncThunk(
  'profile/getProfileCompletion',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.getProfileCompletion();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- LANGUAGE --------

/**
 * Update user language preference
 * PUT /api/profile/language
 */
export const updateLanguage = createAsyncThunk(
  'profile/updateLanguage',
  async (language, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.updateLanguage(language);
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// -------- ACCOUNT MANAGEMENT --------

/**
 * Delete user account (soft delete)
 * DELETE /api/profile/account
 */
export const deleteAccount = createAsyncThunk(
  'profile/deleteAccount',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.deleteAccount();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

/**
 * Reactivate deleted account
 * POST /api/profile/account/reactivate
 */
export const reactivateAccount = createAsyncThunk(
  'profile/reactivateAccount',
  async (_, { rejectWithValue, getState }) => {
    try {
      const state = getState();
      const isAuthenticated = state.auth?.isAuthenticated || false;
      
      if (!isAuthenticated) {
        return rejectWithValue('User not authenticated');
      }
      
      const response = await ProfileService.reactivateAccount();
      return response;
    } catch (error) {
      return rejectWithValue(handleError(error));
    }
  }
);

// ==================== SLICE ====================

const initialState = {
  // Profile data - all null by default (empty)
  profile: null,
  publicProfile: null,
  settings: null,
  notificationPreferences: null,
  activity: null,
  profileCompletion: null,
  
  // UI states
  loading: false,
  updating: false,
  uploadingImage: false,
  deletingImage: false,
  
  // Error states
  error: null,
  updateError: null,
  imageError: null,
  
  // Success states
  updateSuccess: false,
  imageUpdateSuccess: false,
  imageRemoveSuccess: false,
  
  // Form states
  isEditing: false,
  formData: {},
  
  // Track if profile has been loaded
  profileLoaded: false,
};

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    // Reset all profile data to null (empty)
    resetProfileState: (state) => {
      state.profile = null;
      state.publicProfile = null;
      state.settings = null;
      state.notificationPreferences = null;
      state.activity = null;
      state.profileCompletion = null;
      state.error = null;
      state.updateError = null;
      state.imageError = null;
      state.updateSuccess = false;
      state.imageUpdateSuccess = false;
      state.imageRemoveSuccess = false;
      state.isEditing = false;
      state.formData = {};
      state.profileLoaded = false;
      state.loading = false;
      state.updating = false;
      state.uploadingImage = false;
      state.deletingImage = false;
    },
    
    // Clear errors
    clearProfileErrors: (state) => {
      state.error = null;
      state.updateError = null;
      state.imageError = null;
    },
    
    // Set editing mode
    setEditing: (state, action) => {
      state.isEditing = action.payload;
      if (action.payload && state.profile) {
        state.formData = { ...state.profile };
      } else if (!action.payload) {
        state.formData = {};
      }
    },
    
    // Update form data
    updateFormData: (state, action) => {
      state.formData = { ...state.formData, ...action.payload };
    },
    
    // Reset form data
    resetFormData: (state) => {
      state.formData = {};
      state.isEditing = false;
    },
    
    // Update profile locally (optimistic update)
    updateLocalProfile: (state, action) => {
      if (state.profile) {
        state.profile = { ...state.profile, ...action.payload };
      }
    },
    
    // Clear success states
    clearProfileSuccess: (state) => {
      state.updateSuccess = false;
      state.imageUpdateSuccess = false;
      state.imageRemoveSuccess = false;
    },
    
    // Set profile (for auth integration)
    setProfile: (state, action) => {
      state.profile = action.payload;
      state.profileLoaded = true;
    },
    
    // Update language preference
    updateLanguageLocal: (state, action) => {
      if (state.profile) {
        state.profile.language = action.payload;
      }
      if (state.settings) {
        state.settings.language = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== GET PROFILE ==========
      .addCase(getProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.loading = false;
        
        const profileData = action.payload.data;
        
        // If we already have a profile with images, preserve them
        if (state.profile && profileData) {
          if (!profileData.profileImage?.url && state.profile.profileImage?.url) {
            profileData.profileImage = state.profile.profileImage;
          }
          if (profileData.providerProfile && state.profile.providerProfile) {
            if (!profileData.providerProfile.profileImage?.url && state.profile.providerProfile.profileImage?.url) {
              profileData.providerProfile.profileImage = state.profile.providerProfile.profileImage;
            }
          }
        }
        
        state.profile = profileData || null;
        state.profileLoaded = true;
        state.error = null;
      })
      .addCase(getProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get profile';
        state.profileLoaded = false;
        
        if (action.payload === 'User not authenticated' || action.payload === 'Session expired') {
          state.profile = null;
        }
      })
      
      // ========== UPDATE PROFILE INFO ==========
      .addCase(updateProfileInfo.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateProfileInfo.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.profile = action.payload.data;
        state.isEditing = false;
        state.formData = {};
        state.profileLoaded = true;
      })
      .addCase(updateProfileInfo.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update profile';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE PROFILE IMAGE ==========
      .addCase(updateProfileImage.pending, (state) => {
        state.uploadingImage = true;
        state.imageError = null;
        state.imageUpdateSuccess = false;
      })
      .addCase(updateProfileImage.fulfilled, (state, action) => {
        state.uploadingImage = false;
        state.imageUpdateSuccess = true;
        if (state.profile) {
          state.profile.profileImage = action.payload.data;
        }
      })
      .addCase(updateProfileImage.rejected, (state, action) => {
        state.uploadingImage = false;
        state.imageError = action.payload || 'Failed to update profile image';
        state.imageUpdateSuccess = false;
      })
      
      // ========== REMOVE PROFILE IMAGE ==========
      .addCase(removeProfileImage.pending, (state) => {
        state.deletingImage = true;
        state.imageError = null;
        state.imageRemoveSuccess = false;
      })
      .addCase(removeProfileImage.fulfilled, (state) => {
        state.deletingImage = false;
        state.imageRemoveSuccess = true;
        if (state.profile) {
          state.profile.profileImage = {
            url: null,
            publicId: null,
            thumbnail: null,
            uploadedAt: null
          };
        }
      })
      .addCase(removeProfileImage.rejected, (state, action) => {
        state.deletingImage = false;
        state.imageError = action.payload || 'Failed to remove profile image';
        state.imageRemoveSuccess = false;
      })
      
      // ========== GET PUBLIC PROFILE ==========
      .addCase(getPublicProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPublicProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.publicProfile = action.payload.data;
      })
      .addCase(getPublicProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get public profile';
      })
      
      // ========== GET PUBLIC USER BY EMAIL ==========
      .addCase(getPublicUserByEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPublicUserByEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.publicProfile = action.payload.data;
      })
      .addCase(getPublicUserByEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get user by email';
      })
      
      // ========== GET USER SETTINGS ==========
      .addCase(getUserSettings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserSettings.fulfilled, (state, action) => {
        state.loading = false;
        state.settings = action.payload.data;
      })
      .addCase(getUserSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get settings';
      })
      
      // ========== UPDATE SETTINGS ==========
      .addCase(updateSettings.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateSettings.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.settings = action.payload.data;
        if (state.profile && action.payload.data.language) {
          state.profile.language = action.payload.data.language;
        }
      })
      .addCase(updateSettings.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update settings';
        state.updateSuccess = false;
      })
      
      // ========== UPDATE NOTIFICATION PREFERENCES ==========
      .addCase(updateNotificationPreferences.pending, (state) => {
        state.updating = true;
        state.updateError = null;
        state.updateSuccess = false;
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.notificationPreferences = action.payload.data;
        if (state.settings) {
          state.settings.notifications = action.payload.data;
        }
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update notification preferences';
        state.updateSuccess = false;
      })
      
      // ========== GET USER ACTIVITY ==========
      .addCase(getUserActivity.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserActivity.fulfilled, (state, action) => {
        state.loading = false;
        state.activity = action.payload.data;
      })
      .addCase(getUserActivity.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get activity';
      })
      
      // ========== UPDATE USER ACTIVITY ==========
      .addCase(updateUserActivity.pending, (state) => {
        state.updating = true;
        state.error = null;
      })
      .addCase(updateUserActivity.fulfilled, (state, action) => {
        state.updating = false;
        state.activity = action.payload.data;
      })
      .addCase(updateUserActivity.rejected, (state, action) => {
        state.updating = false;
        state.error = action.payload || 'Failed to update activity';
      })
      
      // ========== GET PROFILE COMPLETION ==========
      .addCase(getProfileCompletion.pending, (state) => {
        if (!state.loading) {
          state.loading = true;
        }
        state.error = null;
      })
      .addCase(getProfileCompletion.fulfilled, (state, action) => {
        state.loading = false;
        state.profileCompletion = action.payload.data;
      })
      .addCase(getProfileCompletion.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to get profile completion';
      })
      
      // ========== UPDATE LANGUAGE ==========
      .addCase(updateLanguage.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(updateLanguage.fulfilled, (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        if (state.profile) {
          state.profile.language = action.payload.data.language;
        }
        if (state.settings) {
          state.settings.language = action.payload.data.language;
        }
      })
      .addCase(updateLanguage.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to update language';
      })
      
      // ========== DELETE ACCOUNT ==========
      .addCase(deleteAccount.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.updating = false;
        state.updateSuccess = true;
        state.profile = null;
        state.profileLoaded = false;
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to delete account';
      })
      
      // ========== REACTIVATE ACCOUNT ==========
      .addCase(reactivateAccount.pending, (state) => {
        state.updating = true;
        state.updateError = null;
      })
      .addCase(reactivateAccount.fulfilled, (state) => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addCase(reactivateAccount.rejected, (state, action) => {
        state.updating = false;
        state.updateError = action.payload || 'Failed to reactivate account';
      });
  }
});

// ==================== SELECTORS ====================

// Base selector
const selectProfileState = (state) => state.profile;

// Profile data selectors
export const selectProfile = createSelector(
  selectProfileState,
  (profileState) => profileState.profile || null
);

export const selectPublicProfile = createSelector(
  selectProfileState,
  (profileState) => profileState.publicProfile || null
);

export const selectSettings = createSelector(
  selectProfileState,
  (profileState) => profileState.settings || null
);

export const selectNotificationPreferences = createSelector(
  selectProfileState,
  (profileState) => profileState.notificationPreferences || null
);

export const selectActivity = createSelector(
  selectProfileState,
  (profileState) => profileState.activity || null
);

export const selectProfileCompletion = createSelector(
  selectProfileState,
  (profileState) => profileState.profileCompletion || null
);

// Loading selectors
export const selectProfileLoading = createSelector(
  selectProfileState,
  (profileState) => profileState.loading || false
);

export const selectProfileUpdating = createSelector(
  selectProfileState,
  (profileState) => profileState.updating || false
);

export const selectProfileUploadingImage = createSelector(
  selectProfileState,
  (profileState) => profileState.uploadingImage || false
);

export const selectProfileDeletingImage = createSelector(
  selectProfileState,
  (profileState) => profileState.deletingImage || false
);

// Error selectors
export const selectProfileError = createSelector(
  selectProfileState,
  (profileState) => profileState.error || null
);

export const selectProfileUpdateError = createSelector(
  selectProfileState,
  (profileState) => profileState.updateError || null
);

export const selectProfileImageError = createSelector(
  selectProfileState,
  (profileState) => profileState.imageError || null
);

// Success selectors
export const selectProfileUpdateSuccess = createSelector(
  selectProfileState,
  (profileState) => profileState.updateSuccess || false
);

export const selectProfileImageUpdateSuccess = createSelector(
  selectProfileState,
  (profileState) => profileState.imageUpdateSuccess || false
);

export const selectProfileImageRemoveSuccess = createSelector(
  selectProfileState,
  (profileState) => profileState.imageRemoveSuccess || false
);

// Form selectors
export const selectIsEditing = createSelector(
  selectProfileState,
  (profileState) => profileState.isEditing || false
);

export const selectFormData = createSelector(
  selectProfileState,
  (profileState) => profileState.formData || {}
);

// Profile loaded status
export const selectProfileLoaded = createSelector(
  selectProfileState,
  (profileState) => profileState.profileLoaded || false
);

// Derived selectors
export const selectProfileDisplayName = createSelector(
  selectProfile,
  (profile) => profile?.name || 'Guest'
);

export const selectProfileAvatar = createSelector(
  selectProfile,
  (profile) => {
    if (!profile) return null;
    return profile?.profileImage?.url || 
      (profile?.name ? `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=random&size=200` : null);
  }
);

export const selectIsProfileComplete = createSelector(
  [selectProfile, selectProfileCompletion],
  (profile, completion) => {
    if (!profile) return false;
    if (completion) {
      return completion.percentage === 100;
    }
    return false;
  }
);

export const selectProfileCompletionPercentage = createSelector(
  selectProfileCompletion,
  (completion) => completion?.percentage || 0
);

export const selectProfileMissingFields = createSelector(
  selectProfileCompletion,
  (completion) => completion?.missingFields || []
);

export const selectProfileJoinedDate = createSelector(
  selectProfile,
  (profile) => profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString() : null
);

// ==================== EXPORTS ====================

export const {
  resetProfileState,
  clearProfileErrors,
  setEditing,
  updateFormData,
  resetFormData,
  updateLocalProfile,
  clearProfileSuccess,
  setProfile,
  updateLanguageLocal,
} = profileSlice.actions;

export default profileSlice.reducer;